import { create } from 'zustand';

/**
 * A node the mounted canvas's run is paused at, waiting on someone.
 *
 * Carries the session rather than the run: the editor knows the live
 * session directly, and that is what a node chat is created against.
 */
export interface CanvasPausedNode {
  workflowId: string;
  sessionId: string;
  nodeId: string;
  displayName?: string;
  /** What the node put up for review, when the pause said. */
  reviewContent?: string;
  /** When the node last said it was waiting; it says so after each regeneration. */
  announcedAt?: number;
  /** Reopens the node's output in the canvas's preview panel. */
  openPreview?: () => void;
}

interface CanvasPauseBridgeState {
  pausedNode: CanvasPausedNode | null;
  setPausedNode: (node: CanvasPausedNode | null) => void;
  /** Clears the pause, or only the pause on `nodeId` when given. */
  clearPausedNode: (nodeId?: string) => void;
}

/**
 * Connects a chat rail to the workflow canvas mounted beside it, like
 * `canvas-run-bridge-store`.
 *
 * The chat's own run card publishes a pause for runs it started and is still
 * following. That leaves out every run the chat did not start or has lost
 * track of -- a run reopened from Past Runs, a run started before a reload --
 * while the canvas beside it knows the node is waiting. The canvas publishes
 * that here, so a paused node is always answerable from the rail.
 */
export const useCanvasPauseBridgeStore = create<CanvasPauseBridgeState>(
  (set) => ({
    pausedNode: null,
    setPausedNode: (pausedNode) => set({ pausedNode }),
    clearPausedNode: (nodeId) =>
      set((state) =>
        nodeId && state.pausedNode?.nodeId !== nodeId
          ? state
          : { pausedNode: null },
      ),
  }),
);
